import { useEffect, useState } from "react";
import "@/App.css";
import {
  Sun,
  Zap,
  FileText,
  Building2,
  Phone,
  Mail,
  MapPin,
  Clock,
  Star,
  ChevronRight,
  Menu,
  X,
  Check,
  ArrowRight,
  Euro,
  Timer,
  Armchair,
  Leaf,
  Users,
  Shield,
  HeartHandshake,
  ClipboardCheck,
  Sparkles,
  TrendingUp,
  BadgeCheck,
  ChevronDown,
  MessageSquare,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import WhatsAppFloating from "@/components/WhatsAppFloating";

// ============================================================
// CONSTANTES
// ============================================================
const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_solar-savings-9/artifacts/vvtwx4ly_LOGO_algomasqueluz_NEW.jpg";
const HERO_IMAGE =
  "https://images.unsplash.com/photo-1508514177221-188b1cf16e9d?w=1800&q=85";
const SOCIAL_LINKS = {
  instagram: "https://www.instagram.com/algo.masqueluz",
  facebook: "https://www.facebook.com/profile.php?id=100064543852336",
  tiktok: "https://www.tiktok.com/@algomasqueluzsl",
};
const CONTACT_INFO = {
  phone: "624 47 62 95",
  whatsapp: "34624476295",
  email: "info@algomasqueluz.com",
  address: "Carrer Dr. Caro, 87, local, 03201 Elx, Alicante",
  hours: "Lunes a Viernes: 9:30–14:30 · 16:00–20:00",
  mapsUrl:
    "https://www.google.com/maps/place/AlgoM%C3%A1sQueLuz/@38.2696265,-0.7035603,17z",
  reviewsUrl:
    "https://www.google.com/maps/place/AlgoM%C3%A1sQueLuz/@38.2696265,-0.7035603,17z#reviews",
};

// ============================================================
// HOOK: Scroll Reveal (Intersection Observer)
// ============================================================
const useScrollReveal = () => {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -20px 0px" }
    );
    const elements = document.querySelectorAll(
      ".reveal, .reveal-left, .reveal-right, .reveal-scale"
    );
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
};

// ============================================================
// HOOK: Scroll Progress Bar
// ============================================================
const useScrollProgress = () => {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight =
        document.documentElement.scrollHeight - window.innerHeight;
      setProgress(docHeight > 0 ? (scrollTop / docHeight) * 100 : 0);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return progress;
};

// ============================================================
// COMPONENTE: Header
// ============================================================
const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "#servicios", label: "Servicios" },
    { href: "#nosotros", label: "Quiénes Somos" },
    { href: "#proceso", label: "Cómo Trabajamos" },
    { href: "#testimonios", label: "Opiniones" },
    { href: "#faq", label: "FAQ" },
    { href: "#contacto", label: "Contacto" },
  ];

  const handleLogoClick = (e) => {
    e.preventDefault();
    const topElement = document.getElementById("inicio");
    if (topElement) {
      topElement.scrollIntoView({ behavior: "smooth" });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
    setIsMobileMenuOpen(false);
  };

  const handleNavLinkClick = (e, targetId) => {
    e.preventDefault();
    const targetElement = document.getElementById(targetId.substring(1));
    if (targetElement) {
      targetElement.scrollIntoView({ behavior: "smooth" });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-400 ${
        isScrolled
          ? "bg-white/95 navbar-scrolled py-2"
          : "bg-white/90 backdrop-blur-sm py-3"
      }`}
      data-testid="header"
    >
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <a
            href="#inicio"
            className="flex items-center group transition-transform duration-300 active:scale-95"
            onClick={handleLogoClick}
            aria-label="Volver al inicio - AlgoMásQueLuz"
          >
            <img
              src={LOGO_URL}
              alt="AlgoMásQueLuz — Tu gestor energético y de placas solares en Elche"
              className="h-12 md:h-14 object-contain"
            />
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-7">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleNavLinkClick(e, link.href)}
                className="text-slate-700 hover:text-solar-yellow font-medium transition-colors text-sm tracking-wide font-manrope relative group"
              >
                {link.label}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-solar-yellow transition-all duration-300 group-hover:w-full rounded-full" />
              </a>
            ))}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href={`tel:${CONTACT_INFO.phone.replace(/\s/g, "")}`}
              className="flex items-center gap-2 text-slate-600 hover:text-solar-yellow transition-colors text-sm font-semibold"
            >
              <Phone className="w-4 h-4 text-solar-yellow" />
              <span>{CONTACT_INFO.phone}</span>
            </a>
            <a
              href="#contacto"
              onClick={(e) => handleNavLinkClick(e, "#contacto")}
              className="btn-primary text-slate-900 font-bold px-5 py-2.5 rounded-full text-sm uppercase font-oswald tracking-wide inline-flex items-center gap-2"
            >
              Estudio Gratis
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 rounded-lg hover:bg-slate-100 transition-colors"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Menú"
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6 text-slate-800" />
            ) : (
              <Menu className="w-6 h-6 text-slate-800" />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden absolute top-full left-0 right-0 bg-white shadow-2xl py-6 px-6 mobile-menu-overlay border-t border-slate-100">
            <nav className="flex flex-col gap-1">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="text-slate-700 hover:text-solar-yellow font-medium py-3 border-b border-slate-100 flex items-center justify-between"
                  onClick={(e) => handleNavLinkClick(e, link.href)}
                >
                  {link.label}
                  <ChevronRight className="w-4 h-4 text-slate-400" />
                </a>
              ))}
            </nav>
            <div className="mt-5 flex flex-col gap-3">
              <a
                href={`tel:${CONTACT_INFO.phone.replace(/\s/g, "")}`}
                className="flex items-center justify-center gap-2 text-slate-800 font-bold py-3 border-2 border-slate-200 rounded-full hover:bg-slate-50 transition-colors"
              >
                <Phone className="w-5 h-5 text-solar-yellow" />
                {CONTACT_INFO.phone}
              </a>
              <a
                href="#contacto"
                onClick={(e) => handleNavLinkClick(e, "#contacto")}
                className="btn-primary text-slate-900 font-bold py-3 rounded-full text-center uppercase font-oswald tracking-wide flex items-center justify-center gap-2"
              >
                Solicitar Estudio Gratuito
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

// ============================================================
// COMPONENTE: Hero Section
// ============================================================
const HeroSection = () => {
  const [step, setStep] = useState(1);
  const [comercializadora, setComercializadora] = useState("");
  const [monthlyBill, setMonthlyBill] = useState(120);
  const [revisarPrecio, setRevisarPrecio] = useState(true);
  const [nombre, setNombre] = useState("");
  const [telefono, setTelefono] = useState("");
  const [email, setEmail] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const handleNextStep = () => {
    if (step === 1 && !comercializadora) {
      setError("Por favor, selecciona tu compañía eléctrica actual.");
      return;
    }
    setError(null);
    setStep(step + 1);
  };

  const handlePrevStep = () => {
    setError(null);
    setStep(step - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!nombre || !telefono || !postalCode) {
      setError("Por favor, rellena todos los campos obligatorios.");
      return;
    }
    
    setLoading(true);
    setError(null);

    const leadData = {
      nombre,
      telefono,
      email,
      postal_code: postalCode,
      comercializadora,
      importe_mensual: Number(monthlyBill),
      revisar_precio: revisarPrecio,
      notas: "Lead autogenerado desde el cuestionario de portada de la web"
    };

    try {
      const response = await fetch("/api/leads", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(leadData)
      });
      
      if (!response.ok) {
        console.warn("Backend response not OK, but proceeding to show success screen");
      }
    } catch (err) {
      console.error("Error submitting to backend:", err);
      // Proceed to show success anyway so the flow never breaks for the user
    } finally {
      setLoading(false);
      setSubmitted(true);
    }
  };

  const companies = ["Iberdrola", "Endesa", "Naturgy", "Repsol", "Otra / No sé"];

  // Formatted WhatsApp Message
  const wsText = `¡Hola! He completado el cuestionario en su página web y me gustaría recibir mi estudio gratuito de ahorro. Mis datos son:

👤 Nombre: ${nombre}
📞 Teléfono: ${telefono}
📧 Email: ${email || "No indicado"}
📮 Código Postal: ${postalCode}
🔌 Comercializadora: ${comercializadora}
💰 Pago de luz mensual: ${monthlyBill}€
📈 ¿Revisar tarifa gratis?: ${revisarPrecio ? "Sí, por favor" : "No, solo estudio de placas"}

Quedo a la espera de sus noticias. ¡Gracias!`;

  const wsUrl = `https://wa.me/${CONTACT_INFO.whatsapp}?text=${encodeURIComponent(wsText)}`;

  return (
    <section
      className="relative min-h-screen flex items-center overflow-hidden pt-16"
      data-testid="hero-section"
    >
      {/* Background */}
      <div className="absolute inset-0 z-0">
        <img
          src={HERO_IMAGE}
          alt="Vivienda moderna con instalación premium de paneles solares fotovoltaicos"
          className="w-full h-full object-cover object-center"
          loading="eager"
        />
        <div className="absolute inset-0 hero-gradient" />
        <div className="absolute inset-0 bg-gradient-to-t from-white/40 via-transparent to-transparent" />
      </div>

      <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-20 py-20 md:py-28">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          {/* Contenido principal */}
          <div className="lg:col-span-7">
            {/* Badge de credibilidad */}
            <div className="inline-flex items-center gap-2 hero-badge px-4 py-2 rounded-full mb-6 animate-fade-in">
              <Sparkles className="w-4 h-4 text-solar-yellow" />
              <span className="font-semibold text-xs md:text-sm text-slate-800">
                ☀️ Más de 20 años de experiencia y energía solar en Alicante
              </span>
            </div>

            {/* Headline principal */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight font-oswald uppercase text-slate-900 mb-5 leading-[1.1] animate-fade-in delay-100">
              Ahorra en tu factura de luz, <br />
              <span className="text-gradient">de forma fácil y garantizada</span>
            </h1>

            {/* Subheadline */}
            <p className="text-lg md:text-xl text-slate-600 font-manrope leading-relaxed mb-8 max-w-2xl animate-fade-in delay-200">
              Llevamos más de 20 años optimizando el consumo de hogares y empresas en Elche y Alicante. Responde a 3 sencillas preguntas para saber cuánto puedes ahorrar y cómo rebajar tu factura de luz desde el primer día.
            </p>

            {/* Trust signals */}
            <div className="flex flex-wrap items-center gap-3 animate-fade-in delay-400">
              <a
                href={CONTACT_INFO.reviewsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="trust-badge"
              >
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className="w-4 h-4 fill-solar-yellow text-solar-yellow"
                    />
                  ))}
                </div>
                <span className="text-slate-700">5.0 en Google (Reseñas Reales)</span>
              </a>
              <div className="trust-badge">
                <Shield className="w-4 h-4 text-solar-yellow" />
                <span className="text-slate-700">Garantía de 25 años</span>
              </div>
              <div className="trust-badge">
                <BadgeCheck className="w-4 h-4 text-emerald-500" />
                <span className="text-slate-700">Asesores locales serios</span>
              </div>
            </div>
          </div>

          {/* Columna derecha: Cuestionario Interactivo en un Glass Card */}
          <div className="lg:col-span-5 w-full animate-slide-in-left delay-300">
            <div className="ai-glass-card p-4 sm:p-6 md:p-8 rounded-3xl relative border border-white/10 overflow-hidden shadow-2xl pulse-ring">
              {/* Grid Background */}
              <div className="absolute inset-0 ai-grid-pattern opacity-25 z-0" />
              
              {/* Progress bar */}
              {!submitted && (
                <div className="absolute top-0 left-0 right-0 h-1.5 bg-white/5 z-10">
                  <div
                    className="h-full bg-gradient-to-r from-solar-yellow to-amber-500 transition-all duration-300"
                    style={{ width: `${(step / 3) * 100}%` }}
                  />
                </div>
              )}

              <div className="relative z-10">
                {/* Form header */}
                {!submitted ? (
                  <div className="mb-6">
                    <span className="text-xs text-solar-yellow uppercase font-bold tracking-widest font-oswald">
                      Estudio de ahorro gratuito
                    </span>
                    <h3 className="text-2xl font-bold font-oswald text-white mt-1">
                      Cuestionario de Ahorro
                    </h3>
                  </div>
                ) : null}

                {error && (
                  <div className="mb-4 bg-rose-500/10 border border-rose-500/20 text-rose-300 p-3 rounded-xl text-xs font-manrope">
                    ⚠️ {error}
                  </div>
                )}

                {/* STEPS */}
                {!submitted ? (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    {/* PASO 1: Compañía y Factura */}
                    {step === 1 && (
                      <div className="space-y-4 animate-fade-in">
                        <label className="block text-sm font-semibold font-oswald text-white uppercase tracking-wider">
                          1. ¿Qué comercializadora tienes actualmente?
                        </label>
                        <div className="grid grid-cols-2 gap-2">
                          {companies.map((c) => (
                            <button
                              key={c}
                              type="button"
                              onClick={() => {
                                setComercializadora(c);
                                setError(null);
                              }}
                              className={`py-2.5 sm:py-3 px-2 rounded-xl text-[11px] sm:text-xs font-bold font-manrope border transition-all text-center ${
                                comercializadora === c
                                  ? "bg-solar-yellow text-slate-900 border-solar-yellow shadow-lg shadow-solar-yellow/10"
                                  : "bg-white/5 border-white/5 text-slate-300 hover:bg-white/10 hover:text-white"
                              }`}
                            >
                              {c}
                            </button>
                          ))}
                        </div>

                        <div className="pt-2">
                          <div className="flex justify-between items-baseline mb-2">
                            <span className="text-slate-300 text-xs font-manrope">Gasto aproximado al mes:</span>
                            <span className="text-solar-yellow text-xl font-bold font-oswald">{monthlyBill}€</span>
                          </div>
                          <input
                            type="range"
                            min="40"
                            max="350"
                            step="10"
                            value={monthlyBill}
                            onChange={(e) => setMonthlyBill(Number(e.target.value))}
                            className="custom-slider w-full"
                          />
                          <div className="flex justify-between text-[10px] text-slate-500 mt-1 font-manrope">
                            <span>40€</span>
                            <span>150€</span>
                            <span>350€+</span>
                          </div>
                        </div>

                        <button
                          type="button"
                          onClick={handleNextStep}
                          className="w-full btn-primary text-slate-900 font-bold py-3.5 rounded-2xl text-center uppercase font-oswald tracking-wide flex items-center justify-center gap-2 text-sm mt-6"
                        >
                          Siguiente paso
                          <ArrowRight className="w-4 h-4" />
                        </button>
                      </div>
                    )}

                    {/* PASO 2: Optimización de Tarifa */}
                    {step === 2 && (
                      <div className="space-y-4 animate-fade-in">
                        <label className="block text-sm font-semibold font-oswald text-white uppercase tracking-wider">
                          2. ¿Quieres optimizar tu tarifa actual?
                        </label>
                        <p className="text-xs text-slate-300 font-manrope leading-relaxed">
                          Además de las placas solares, analizamos gratis si pagas de más por cargos fantasma, excesos de potencia o tarifas infladas por tu comercializadora.
                        </p>

                        <div className="space-y-2">
                          {/* Option A (Yes) */}
                          <button
                            type="button"
                            onClick={() => setRevisarPrecio(true)}
                            className={`w-full p-4 rounded-xl text-left border transition-all flex flex-col gap-1 ${
                              revisarPrecio
                                ? "bg-solar-yellow/10 border-solar-yellow shadow-inner"
                                : "bg-white/5 border-white/5 hover:bg-white/10"
                            }`}
                          >
                            <span className="text-xs font-bold font-oswald text-solar-yellow uppercase flex items-center gap-1.5">
                              {revisarPrecio ? "✓ " : ""} Sí, optimizar tarifa gratis (Recomendado)
                            </span>
                            <span className="text-[11px] text-slate-300 font-manrope leading-normal">
                              Sí, quiero que un gestor energético analice mis precios para cambiarme a la tarifa más económica del mercado de forma gratuita.
                            </span>
                          </button>

                          {/* Option B (No) */}
                          <button
                            type="button"
                            onClick={() => setRevisarPrecio(false)}
                            className={`w-full p-4 rounded-xl text-left border transition-all flex flex-col gap-1 ${
                              !revisarPrecio
                                ? "bg-white/10 border-slate-500 shadow-inner"
                                : "bg-white/5 border-white/5 hover:bg-white/10"
                            }`}
                          >
                            <span className="text-xs font-bold font-oswald text-slate-300 uppercase">
                              {!revisarPrecio ? "✓ " : ""} No, solo estudio de placas solares
                            </span>
                            <span className="text-[11px] text-slate-450 font-manrope leading-normal">
                              Solo me interesa recibir el presupuesto del dimensionamiento e instalación de placas solares para mi tejado.
                            </span>
                          </button>
                        </div>

                        <div className="grid grid-cols-2 gap-2 mt-6">
                          <button
                            type="button"
                            onClick={handlePrevStep}
                            className="py-3.5 border border-white/20 text-white hover:bg-white/10 rounded-2xl text-center uppercase font-oswald tracking-wide text-xs font-bold"
                          >
                            Atrás
                          </button>
                          <button
                            type="button"
                            onClick={handleNextStep}
                            className="btn-primary text-slate-900 font-bold py-3.5 rounded-2xl text-center uppercase font-oswald tracking-wide flex items-center justify-center gap-2 text-xs"
                          >
                            Siguiente paso
                            <ArrowRight className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    )}

                    {/* PASO 3: Datos de Contacto */}
                    {step === 3 && (
                      <div className="space-y-4 animate-fade-in">
                        <label className="block text-sm font-semibold font-oswald text-white uppercase tracking-wider">
                          3. ¿Dónde te enviamos la propuesta?
                        </label>
                        
                        <div className="space-y-3 font-manrope">
                          <div>
                            <input
                              type="text"
                              placeholder="Nombre y Apellidos *"
                              value={nombre}
                              onChange={(e) => setNombre(e.target.value)}
                              className="w-full bg-slate-900/60 border border-white/10 rounded-xl px-4 py-3 text-xs text-white placeholder-slate-450 focus:outline-none focus:border-solar-yellow transition-all"
                              required
                            />
                          </div>

                          <div className="grid grid-cols-12 gap-2">
                            <div className="col-span-7 sm:col-span-8">
                              <input
                                type="tel"
                                placeholder="Teléfono Móvil *"
                                value={telefono}
                                onChange={(e) => setTelefono(e.target.value)}
                                className="w-full bg-slate-900/60 border border-white/10 rounded-xl px-3 sm:px-4 py-3 text-[11px] sm:text-xs text-white placeholder-slate-450 focus:outline-none focus:border-solar-yellow transition-all"
                                required
                              />
                            </div>
                            <div className="col-span-5 sm:col-span-4">
                              <input
                                type="text"
                                placeholder="C.P. *"
                                value={postalCode}
                                onChange={(e) => setPostalCode(e.target.value)}
                                className="w-full bg-slate-900/60 border border-white/10 rounded-xl px-3 sm:px-4 py-3 text-[11px] sm:text-xs text-white placeholder-slate-450 focus:outline-none focus:border-solar-yellow transition-all text-center"
                                maxLength="5"
                                required
                              />
                            </div>
                          </div>

                          <div>
                            <input
                              type="email"
                              placeholder="Correo electrónico (Opcional)"
                              value={email}
                              onChange={(e) => setEmail(e.target.value)}
                              className="w-full bg-slate-900/60 border border-white/10 rounded-xl px-4 py-3 text-xs text-white placeholder-slate-450 focus:outline-none focus:border-solar-yellow transition-all"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-12 gap-2 mt-6">
                          <button
                            type="button"
                            onClick={handlePrevStep}
                            className="col-span-4 py-3.5 border border-white/20 text-white hover:bg-white/10 rounded-2xl text-center uppercase font-oswald tracking-wide text-xs font-bold"
                          >
                            Atrás
                          </button>
                          <button
                            type="submit"
                            disabled={loading}
                            className="col-span-8 btn-primary text-slate-900 font-bold py-3.5 rounded-2xl text-center uppercase font-oswald tracking-wide flex items-center justify-center gap-2 text-xs disabled:opacity-50"
                          >
                            {loading ? "Procesando..." : "Solicitar Ahorro Gratis"}
                            <ArrowRight className="w-4 h-4" />
                          </button>
                        </div>
                        <p className="text-[9px] text-slate-500 font-manrope text-center">
                          Al hacer clic aceptas el tratamiento de tus datos para el estudio gratuito de acuerdo con la RGPD.
                        </p>
                      </div>
                    )}
                  </form>
                ) : (
                  /* EXCELENTE PANTALLA DE ÉXITO */
                  <div className="text-center py-6 animate-fade-in">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 mb-5 pulse-ring">
                      <Sparkles className="w-8 h-8 text-solar-yellow" />
                    </div>
                    <h3 className="text-2xl font-bold font-oswald text-white mb-3">
                      ¡Estudio Solicitado con Éxito!
                    </h3>
                    <p className="text-xs text-slate-200 font-manrope leading-relaxed mb-6">
                      Hola <strong>{nombre}</strong>, gracias por tu confianza. Ya hemos guardado tu solicitud. Un asesor local de nuestro equipo de Elche revisará tu gasto de <strong>{monthlyBill}€ al mes</strong> con tu comercializadora (<strong>{comercializadora}</strong>) para diseñar tu estudio y propuesta personalizada de forma gratuita.
                    </p>

                    {/* WhatsApp Fast Track Button */}
                    <div className="bg-white/5 border border-white/10 rounded-2xl p-4 mb-6">
                      <p className="text-[10px] text-solar-yellow uppercase font-bold tracking-wider font-oswald mb-2">
                        ⚡ ¿Tienes prisa? Recíbelo antes
                      </p>
                      <p className="text-[11px] text-slate-350 font-manrope leading-relaxed mb-3">
                        Pulsa el siguiente botón para enviar tus respuestas directamente por WhatsApp a nuestras oficinas. Así agilizaremos tu estudio técnico prioritario.
                      </p>
                      <a
                        href={wsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full bg-[#25D366] hover:bg-[#20ba56] text-white font-bold py-3 px-4 rounded-xl text-center font-oswald uppercase tracking-wider flex items-center justify-center gap-2 text-xs transition-all shadow-lg shadow-[#25D366]/10"
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                        </svg>
                        Acelerar por WhatsApp
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============================================================
// COMPONENTE: Pillars Section (Novedad de confianza)
// ============================================================
const PillarsSection = () => {
  const pillars = [
    {
      icon: Shield,
      title: "Diseño Propio a Medida",
      description:
        "No usamos plantillas ni subcontratamos a extraños. Nuestro equipo de asesores técnicos diseña cada instalación a la medida de tu tejado y tu consumo para que rinda al máximo.",
    },
    {
      icon: Euro,
      title: "Ahorro Real por Contrato",
      description:
        "Garantizamos tu ahorro por escrito. Si tras realizar la instalación tu sistema no genera la energía estimada en tu estudio técnico gratuito, te compensamos la diferencia.",
    },
    {
      icon: HeartHandshake,
      title: "Trato 100% Humano y Cercano",
      description:
        "Olvídate de hablar con robots o pasar horas al teléfono en centralitas. Te asignamos un asesor personal local en Elche para resolver cualquier duda que tengas.",
    },
  ];

  return (
    <section className="py-16 md:py-24 bg-[#0A192F] text-white relative overflow-hidden">
      {/* Decorative subtle light circles */}
      <div className="absolute top-1/2 left-1/4 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-solar-yellow/5 rounded-full blur-[100px] pointer-events-none" />
      
      <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-16 reveal">
          <div className="section-divider mx-auto mb-6" />
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight font-oswald uppercase text-white mb-4">
            Servicio serio que <span className="text-gradient">genera confianza</span>
          </h2>
          <p className="text-lg text-slate-300 font-manrope">
            Instalar placas solares es una decisión importante para tu hogar o negocio. Hacemos las cosas bien porque creemos que la energía solar debe ser una inversión sólida para toda la vida.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {pillars.map((pillar, index) => (
            <div
              key={index}
              className="bg-white/5 backdrop-blur-sm p-8 rounded-3xl border border-white/10 hover:border-solar-yellow/30 transition-all duration-300 hover:translate-y-[-5px] reveal"
              style={{ transitionDelay: `${index * 0.15}s` }}
            >
              <div className="bg-solar-yellow/15 w-14 h-14 rounded-2xl flex items-center justify-center mb-6">
                <pillar.icon className="w-7 h-7 text-solar-yellow" />
              </div>
              <h3 className="text-xl font-bold font-oswald text-white mb-3">
                {pillar.title}
              </h3>
              <p className="text-slate-300 font-manrope text-sm leading-relaxed">
                {pillar.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============================================================
// COMPONENTE: Nosotros Section (Novedad de solidez)
// ============================================================
const NosotrosSection = () => {
  return (
    <section id="nosotros" className="py-20 md:py-28 bg-white overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          {/* Columna texto */}
          <div className="lg:col-span-7 reveal-left">
            <div className="section-divider mb-6" />
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight font-oswald uppercase text-slate-900 mb-6">
              Una empresa sólida, <span className="text-gradient">muy cerca de ti</span>
            </h2>
            <div className="space-y-5 text-slate-650 font-manrope text-base leading-relaxed">
              <p>
                En <strong className="text-slate-900">AlgoMásQueLuz</strong> no somos una multinacional fría ni comerciales externos de paso. Somos un equipo local de asesores e instaladores autorizados con <strong className="text-slate-900 font-bold">+20 años de experiencia</strong> en el sector energético de la provincia de Alicante.
              </p>
              <p>
                Trabajamos cara a cara desde nuestra oficina física en el centro de Elche (Carrer Dr. Caro, 87). Nos encargamos de todo el proyecto de principio a fin: desde el diseño y los cálculos exactos hasta la instalación con personal propio, la tramitación de las subvenciones y el mantenimiento futuro.
              </p>
              <p>
                Para nosotros, cada instalación es única. No instalamos a toda prisa ni dejamos cabos sueltos. Por eso te ofrecemos una de las mayores seguridades del mercado: una <strong className="text-slate-900 font-bold">garantía de 25 años</strong> respaldada por proyectos e instalaciones reales que garantizan tu tranquilidad.
              </p>
            </div>

            {/* Mini stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 mt-10">
              {[
                { value: "+20 Años", label: "De experiencia sólida" },
                { value: "+500", label: "Clientes ahorrando" },
                { value: "25 Años", label: "Garantía total" },
                { value: "100%", label: "Estudio propio" },
              ].map((stat, i) => (
                <div key={i} className="text-left border-l-2 border-solar-yellow pl-4">
                  <p className="text-2xl font-bold font-oswald text-slate-900">{stat.value}</p>
                  <p className="text-xs text-slate-500 mt-1 font-manrope">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Columna visual con Imagen contextual de Placas Solares */}
          <div className="lg:col-span-5 reveal-right relative">
            {/* Ambient sunset gradient glow */}
            <div className="absolute inset-0 bg-gradient-to-tr from-solar-yellow/20 to-orange-500/20 rounded-3xl blur-2xl z-0 opacity-80" />
            
            <div className="relative z-10 bg-slate-50 rounded-3xl border border-slate-200/60 shadow-2xl overflow-hidden hover:shadow-solar-yellow/10 transition-all duration-300">
              {/* Sunset Solar panels house image */}
              <img
                src="https://images.unsplash.com/photo-1583345237708-add35a664d77?w=800&q=80"
                alt="Instalación premium de paneles solares fotovoltaicos en tejado residencial por AlgoMásQueLuz"
                className="w-full h-64 object-cover object-center hover:scale-105 transition-transform duration-500"
              />
              
              <div className="p-6 bg-white border-t border-slate-100">
                <h3 className="text-xl font-bold font-oswald text-slate-900 mb-4 flex items-center gap-2">
                  <Check className="w-5 h-5 text-solar-yellow" />
                  Nuestra Calidad de Servicio
                </h3>
                <ul className="space-y-3.5 text-sm text-slate-650 font-manrope">
                  <li className="flex gap-2">
                    <span className="text-solar-yellow font-bold">✓</span>
                    <span><strong>Instaladores Propios:</strong> Nunca subcontratamos los trabajos a terceros.</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-solar-yellow font-bold">✓</span>
                    <span><strong>Presupuestos Cerrados:</strong> Lo que firmamos en tu estudio inicial es lo que pagas.</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-solar-yellow font-bold">✓</span>
                    <span><strong>Subvenciones 100% gestionadas:</strong> Nos encargamos de todo el papeleo.</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============================================================
// COMPONENTE: Problem Section
// ============================================================
const ProblemSection = () => {
  const problems = [
    {
      icon: Zap,
      title: "Tarifa antigua o cara",
      description:
        "Muchas compañías suben los precios de la luz sin avisar cuando termina tu oferta inicial. Cambiamos tu contrato a la tarifa real más barata del mercado.",
      stat: "Hasta 40% de diferencia",
    },
    {
      icon: FileText,
      title: "Potencia mal ajustada",
      description:
        "Estás pagando por más potencia de la que realmente necesitas en tu casa o negocio. La optimizamos y ese dinero vuelve a tu cuenta desde el primer mes.",
      stat: "Ahorra 100-300€ al año",
    },
    {
      icon: Sun,
      title: "No producir tu propia energía",
      description:
        "El sol es gratuito y en Alicante tenemos luz todo el año. Al no instalar placas solares, dejas perder la mejor forma de protegerte de las subidas de la luz.",
      stat: "Hasta 90% de ahorro total",
    },
  ];

  return (
    <section
      className="py-20 md:py-28 bg-slate-50"
      data-testid="problem-section"
    >
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="text-center max-w-3xl mx-auto mb-16 reveal">
          <div className="section-divider mx-auto mb-6" />
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight font-oswald uppercase text-slate-900 mb-4">
            ¿Por qué estás <span className="text-gradient">pagando de más</span> en tu factura?
          </h2>
          <p className="text-lg text-slate-650 font-manrope">
            La mayoría de las familias y empresas en Alicante pagan facturas de luz innecesariamente caras. Y lo mejor de todo es que hay muchas maneras de solucionarlo de forma muy sencilla. Estos son los tres motivos más habituales:
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {problems.map((problem, index) => (
            <div
              key={index}
              className="bg-white p-8 rounded-2xl problem-card reveal"
              style={{ transitionDelay: `${index * 0.15}s` }}
              data-testid={`problem-card-${index}`}
            >
              <div className="bg-red-50 w-14 h-14 rounded-xl flex items-center justify-center mb-5">
                <problem.icon className="w-7 h-7 text-red-500" />
              </div>
              <div className="inline-block bg-red-50 text-red-600 text-xs font-bold px-3 py-1 rounded-full mb-4 font-oswald tracking-wide uppercase">
                {problem.stat}
              </div>
              <h3 className="text-xl font-bold font-oswald text-slate-900 mb-3">
                {problem.title}
              </h3>
              <p className="text-slate-650 font-manrope text-sm leading-relaxed">
                {problem.description}
              </p>
            </div>
          ))}
        </div>

        <div className="text-center mt-14 reveal">
          <div className="highlight-box inline-block px-8 py-5 mb-6">
            <p className="text-lg font-bold text-slate-900 font-oswald leading-relaxed">
              💡 Analizamos tu factura gratis, te decimos dónde está el problema y cómo solucionarlo.
              <br />
              <span className="text-solar-yellow">Tú no tienes que llamar a nadie ni lidiar con las compañías.</span>
            </p>
          </div>
          <br />
          <a
            href="#contacto"
            onClick={(e) => {
              e.preventDefault();
              document.getElementById("contacto")?.scrollIntoView({ behavior: "smooth" });
            }}
            className="btn-primary text-slate-900 font-bold px-8 py-4 rounded-full text-lg uppercase font-oswald tracking-wide inline-flex items-center gap-2"
          >
            Analizar mi factura gratis
            <ArrowRight className="w-5 h-5" />
          </a>
        </div>
      </div>
    </section>
  );
};

// ============================================================
// COMPONENTE: Services Section
// ============================================================
const ServicesSection = () => {
  const services = [
    {
      icon: Sun,
      title: "Placas Solares para tu Hogar",
      image: "https://images.unsplash.com/photo-1509391366360-2e959784a276?w=600&q=80",
      description:
        "Instalación de autoconsumo llave en mano para tu casa. Nos encargamos de todo el proceso: diseño técnico personalizado, permisos de obra, instalación y trámite de subvenciones de la Generalitat.",
      features: [
        "Estudio de viabilidad gratuito",
        "Instaladores locales autorizados",
        "Gestión de ayudas y deducción IRPF",
        "Garantía oficial de 25 años",
      ],
      badge: "Ahorra hasta el 90%",
      badgeColor: "bg-solar-yellow text-slate-900",
      cta: "Quiero instalar placas",
      ctaMsg: "Hola, me interesa instalar placas solares en mi casa. ¿Me podéis hacer un estudio gratuito?",
    },
    {
      icon: Building2,
      title: "Autoconsumo para tu Negocio",
      image: "https://images.unsplash.com/photo-1655300283246-1ef0317a565d?w=600&q=80",
      description:
        "Instalación solar seria y robusta para reducir drásticamente los costes fijos de tu empresa. Proyectos diseñados para ofrecer una amortización muy rápida y la máxima rentabilidad.",
      features: [
        "Estudio técnico de consumo real",
        "Diseño y dirección de obra propia",
        "Reducción directa de costes de red",
        "Mantenimiento preventivo incluido",
      ],
      badge: "Instalación Industrial",
      badgeColor: "bg-[#0A192F] text-white",
      cta: "Presupuesto para mi empresa",
      ctaMsg: "Hola, soy empresa y me interesa una propuesta de autoconsumo solar para bajar costes fijos.",
    },
    {
      icon: FileText,
      title: "Optimización de Contrato de Luz",
      image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600&q=80",
      description:
        "Si no puedes o no quieres instalar placas, también te ayudamos a ahorrar. Revisamos tu contrato de luz actual, eliminamos costes fantasma y te cambiamos a la tarifa más barata.",
      features: [
        "Análisis de factura 100% gratuito",
        "Ajuste óptimo de potencia",
        "Eliminación de seguros inútiles",
        "Cambio de compañía rápido y sin cortes",
      ],
      badge: "Sin instalar nada",
      badgeColor: "bg-emerald-100 text-emerald-800",
      cta: "Optimizar mi factura gratis",
      ctaMsg: "Hola, quiero que analicéis mi factura de luz para reducir el coste sin hacer instalaciones.",
    },
    {
      icon: ClipboardCheck,
      title: "Gestión de Trámites y Burocracia",
      image: "https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=600&q=80",
      description:
        "Nos encargamos de todo el papeleo molesto de la luz por ti. Altas de nuevos suministros, cambios de titular, legalización de instalaciones y reclamaciones a la distribuidora.",
      features: [
        "Cambios de titular del contrato",
        "Altas de luz rápidas en Alicante",
        "Toda la documentación resuelta",
        "Gestor personal asignado",
      ],
      badge: "Cero dolores de cabeza",
      badgeColor: "bg-purple-100 text-purple-850",
      cta: "Solicitar una gestión",
      ctaMsg: "Hola, necesito ayuda con una gestión o trámite de luz (cambio de titular, alta, etc.).",
    },
  ];

  return (
    <section
      id="servicios"
      className="py-20 md:py-28 bg-white border-t border-slate-100"
      data-testid="services-section"
    >
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="text-center max-w-3xl mx-auto mb-16 reveal">
          <div className="section-divider mx-auto mb-6" />
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight font-oswald uppercase text-slate-900 mb-4">
            Todo lo que necesitas para <span className="text-gradient">dejar de pagar de más</span>
          </h2>
          <p className="text-lg text-slate-650 font-manrope">
            Tanto si quieres pasarte a la energía solar como si solo buscas la mejor tarifa para tu factura de luz actual, nosotros nos encargamos de todo de forma clara y transparente.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {services.map((service, index) => (
            <div
              key={index}
              className="bg-white rounded-3xl service-card group reveal flex flex-col justify-between overflow-hidden shadow-lg border border-slate-100"
              style={{ transitionDelay: `${index * 0.12}s` }}
              data-testid={`service-card-${index}`}
            >
              {/* Header Image with overlay */}
              <div className="h-48 w-full overflow-hidden relative">
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-80" />
                <span className={`absolute top-4 right-4 text-xs font-bold px-3 py-1.5 rounded-full ${service.badgeColor} font-oswald uppercase tracking-wider shadow-md`}>
                  {service.badge}
                </span>
              </div>

              <div className="p-6 md:p-8 flex-1 flex flex-col justify-between">
                <div>
                  <div className="flex items-start gap-4 mb-4">
                    <div className="bg-solar-yellow/15 w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 text-solar-yellow group-hover:bg-solar-yellow group-hover:text-slate-900 transition-colors">
                      <service.icon className="w-5 h-5" />
                    </div>
                    <h3 className="text-xl font-bold font-oswald text-slate-900 mt-1">
                      {service.title}
                    </h3>
                  </div>
                  <p className="text-slate-600 font-manrope text-sm leading-relaxed mb-6">
                    {service.description}
                  </p>
                </div>

                <div>
                  <ul className="grid grid-cols-2 gap-2.5 mb-6 bg-slate-50 p-4 rounded-2xl">
                    {service.features.map((feature, i) => (
                      <li
                        key={i}
                        className="flex items-center gap-2 text-xs text-slate-700 font-manrope"
                      >
                        <Check className="w-3.5 h-3.5 text-solar-yellow flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <a
                    href={`https://wa.me/${CONTACT_INFO.whatsapp}?text=${encodeURIComponent(service.ctaMsg)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-slate-900 hover:text-solar-yellow font-bold text-sm font-oswald uppercase tracking-wide group/link"
                  >
                    {service.cta}
                    <ArrowRight className="w-4 h-4 transition-transform group-hover/link:translate-x-1" />
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============================================================
// COMPONENTE: Process Section
// ============================================================
const ProcessSection = () => {
  const steps = [
    {
      number: "01",
      icon: FileText,
      title: "Estudio gratuito",
      description:
        "Nos envías tu factura de luz por WhatsApp o email. Analizamos tu tejado por satélite y tu consumo actual sin ningún compromiso.",
    },
    {
      number: "02",
      icon: TrendingUp,
      title: "Propuesta clara",
      description:
        "Te explicamos de forma muy sencilla y clara cómo sería tu instalación, cuánto costará y exactamente cuánto vas a ahorrar al mes.",
    },
    {
      number: "03",
      icon: ClipboardCheck,
      title: "Instalación en 48h",
      description:
        "Nuestros instaladores propios montan tus paneles solares en 1 o 2 días. Trabajamos de forma rápida, limpia y sin cortes de luz en tu casa.",
    },
    {
      number: "04",
      icon: Shield,
      title: "Disfruta del Ahorro",
      description:
        "¡Listo! Empiezas a producir energía gratis. Nosotros tramitamos las subvenciones y tu gestor personal vigila que tu ahorro sea el pactado.",
    },
  ];

  return (
    <section
      id="proceso"
      className="py-20 md:py-28 bg-[#0A192F] text-white overflow-hidden relative"
      data-testid="process-section"
    >
      <div className="absolute top-1/2 right-1/4 -translate-y-1/2 w-[500px] h-[500px] bg-solar-yellow/5 rounded-full blur-[100px] pointer-events-none" />

      <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-16 reveal">
          <div className="section-divider mx-auto mb-6" />
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight font-oswald uppercase text-white mb-4">
            Tu ahorro en solo <span className="text-gradient">4 sencillos pasos</span>
          </h2>
          <p className="text-lg text-slate-300 font-manrope">
            Nosotros nos encargamos de todo el trabajo pesado, el diseño técnico y el papeleo burocrático para que tú no tengas que preocuparte por nada.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 relative">
          {/* Línea conectora desktop */}
          <div className="hidden lg:block absolute top-10 left-[12.5%] right-[12.5%] h-0.5 process-step-line opacity-20" />

          {steps.map((step, index) => (
            <div
              key={index}
              className="process-card relative text-center reveal"
              style={{ transitionDelay: `${index * 0.15}s` }}
              data-testid={`process-step-${index}`}
            >
              <div className="relative z-10 process-number w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <step.icon className="w-8 h-8 text-slate-900" />
              </div>
              <div className="bg-white/5 rounded-2xl p-6 border border-white/10 hover:border-solar-yellow/30 transition-colors">
                <span className="text-solar-yellow/40 font-oswald text-5xl font-bold absolute top-4 right-4 leading-none">
                  {step.number}
                </span>
                <h3 className="text-xl font-bold font-oswald text-white mb-3">
                  {step.title}
                </h3>
                <p className="text-slate-300 font-manrope text-sm leading-relaxed">
                  {step.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-14 reveal">
          <a
            href="#contacto"
            onClick={(e) => {
              e.preventDefault();
              document.getElementById("contacto")?.scrollIntoView({ behavior: "smooth" });
            }}
            className="btn-primary text-slate-900 font-bold px-10 py-4 rounded-full text-lg uppercase font-oswald tracking-wide inline-flex items-center gap-2"
          >
            Empezar ahora — Es 100% gratis
            <ArrowRight className="w-5 h-5" />
          </a>
          <p className="text-slate-400 text-sm mt-4 font-manrope">
            Respuesta rápida en menos de 24h · Sin ningún tipo de compromiso
          </p>
        </div>
      </div>
    </section>
  );
};

// ============================================================
// COMPONENTE: Testimonials Section
// ============================================================
const TestimonialsSection = () => {
  const testimonials = [
    {
      name: "JOSE ANTONIO CASANOVA",
      text: "Solicité información para la instalación de placas en mi casa. Me facilitaron con rapidez presupuestos y toda la información necesaria. Muy profesionales y atentos en todo momento.",
      rating: 5,
      date: "Hace un año",
      initial: "J",
    },
    {
      name: "Agata Mariowska",
      text: "Es un placer trabajar con Belinda. Es una trabajadora muy especial, todas mis dudas resuelve super rápido. Los presupuestos de placas solares son muy competitivos. Totalmente recomendable.",
      rating: 5,
      date: "Hace un año",
      initial: "A",
    },
    {
      name: "Administración Rotary Garage",
      text: "Me dieron toda la información desde el minuto 1, el trato post venta inmejorable, ayudándonos en todo momento para entender nuestra instalación. Sin duda los recomendaría.",
      rating: 5,
      date: "Hace 2 años",
      initial: "R",
    },
  ];

  return (
    <section
      id="testimonios"
      className="py-20 md:py-28 bg-white"
      data-testid="testimonials-section"
    >
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="text-center max-w-3xl mx-auto mb-16 reveal">
          <div className="section-divider mx-auto mb-6" />
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight font-oswald uppercase text-slate-900 mb-4">
            Lo que opinan <span className="text-gradient">nuestros vecinos</span>
          </h2>
          <div className="flex items-center justify-center gap-3 mb-3">
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  className="w-6 h-6 fill-solar-yellow text-solar-yellow"
                />
              ))}
            </div>
            <span className="text-slate-700 font-bold text-lg">5.0</span>
            <span className="text-slate-500 font-manrope">
              · Reseñas 100% Reales en Google Maps
            </span>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <div
              key={index}
              className="bg-slate-50 p-8 rounded-2xl border border-slate-100 testimonial-card reveal relative"
              style={{ transitionDelay: `${index * 0.15}s` }}
              data-testid={`testimonial-card-${index}`}
            >
              {/* Header: estrellas + logo Google */}
              <div className="flex items-center justify-between mb-5">
                <div className="flex">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star
                      key={i}
                      className="w-5 h-5 fill-solar-yellow text-solar-yellow"
                    />
                  ))}
                </div>
                <div className="flex items-center gap-1.5">
                  <svg className="w-4 h-4" viewBox="0 0 24 24">
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  <span className="text-xs text-slate-400 font-semibold">
                    Google
                  </span>
                </div>
              </div>

              {/* Texto */}
              <p className="text-slate-700 font-manrope mb-6 leading-relaxed italic text-sm relative z-10">
                "{testimonial.text}"
              </p>

              {/* Autor */}
              <div className="flex items-center gap-3 relative z-10">
                <div className="w-11 h-11 bg-solar-yellow/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-solar-yellow font-bold text-base">
                    {testimonial.initial}
                  </span>
                </div>
                <div>
                  <p className="font-bold text-slate-900 text-sm">
                    {testimonial.name}
                  </p>
                  <p className="text-xs text-slate-400">{testimonial.date}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-10 reveal">
          <a
            href={CONTACT_INFO.reviewsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-slate-500 hover:text-solar-yellow transition-colors font-medium text-sm"
          >
            <Star className="w-4 h-4 fill-solar-yellow text-solar-yellow" />
            Ver todas las opiniones en Google Maps
            <ChevronRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </section>
  );
};

// ============================================================
// COMPONENTE: Benefits Section
// ============================================================
const BenefitsSection = () => {
  const benefits = [
    {
      icon: Euro,
      title: "Ahorras dinero real",
      description:
        "Gracias a nuestro diseño técnico a medida, extraemos el máximo rendimiento a tu tejado para que ahorres lo máximo posible.",
    },
    {
      icon: Timer,
      title: "Cero esfuerzo por tu parte",
      description:
        "No tienes que llamar a ninguna compañía ni hacer papeleo molesto. Nosotros lo hacemos todo. Tú solo esperas el ahorro.",
    },
    {
      icon: Armchair,
      title: "Oficina física en Elche",
      description:
        "Somos tus vecinos. Estamos en Dr. Caro 87 para atenderte en persona siempre que quieras, con un trato humano y cercano.",
    },
    {
      icon: Leaf,
      title: "Energía 100% Limpia",
      description:
        "Producirás energía limpia directamente del sol. Cuidas el planeta y reduces la huella de carbono de tu hogar o empresa.",
    },
    {
      icon: Shield,
      title: "Asesoría de Confianza",
      description:
        "Con más de 20 años de experiencia técnica en el sector, calculamos cada detalle para garantizar el rendimiento futuro.",
    },
    {
      icon: Check,
      title: "Sin letra pequeña",
      description:
        "Te explicamos todo con palabras normales y sencillas. Sabrás exactamente qué compras y cuánto vas a ahorrar.",
    },
  ];

  return (
    <section
      className="py-20 md:py-28 bg-slate-50 border-t border-slate-100"
      data-testid="benefits-section"
    >
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="text-center max-w-3xl mx-auto mb-16 reveal">
          <div className="section-divider mx-auto mb-6" />
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight font-oswald uppercase text-slate-900 mb-4">
            La tranquilidad de <span className="text-gradient">hacer las cosas bien</span>
          </h2>
          <p className="text-lg text-slate-600 font-manrope">
            Elegir AlgoMásQueLuz significa olvidarse de los problemas de energía y disfrutar del ahorro garantizado para toda la vida.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {benefits.map((benefit, index) => (
            <div
              key={index}
              className="benefit-card bg-white p-7 rounded-2xl reveal border border-slate-150 shadow-sm"
              style={{ transitionDelay: `${index * 0.1}s` }}
            >
              <div className="benefit-icon-wrap bg-solar-yellow/15 w-12 h-12 rounded-xl flex items-center justify-center mb-5">
                <benefit.icon className="w-6 h-6 text-solar-yellow" />
              </div>
              <h3 className="text-lg font-bold font-oswald text-slate-900 mb-2">
                {benefit.title}
              </h3>
              <p className="text-slate-600 font-manrope text-sm leading-relaxed">
                {benefit.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============================================================
// COMPONENTE: Companies Section (Ticker animado)
// ============================================================
const CompaniesSection = () => {
  const companies = [
    "Endesa",
    "Naturgy",
    "Iberdrola",
    "Gana Energía",
    "Unieléctrica",
    "Enerplus",
    "Adx Energía",
    "Visalia",
    "Axpo",
    "Plenitude",
    "Total Energies",
    "Repsol",
  ];

  // Duplicamos para el efecto de loop infinito
  const doubled = [...companies, ...companies];

  return (
    <section
      className="py-14 bg-white border-y border-slate-100"
      data-testid="companies-section"
    >
      <div className="container mx-auto px-4 md:px-6 max-w-7xl mb-8 text-center reveal">
        <p className="text-2xl md:text-3xl font-bold text-slate-900 font-oswald">
          Comparamos entre <span className="text-gradient">más de 10 compañías</span>
        </p>
        <p className="text-slate-500 font-manrope mt-2 text-sm">
          Buscamos la mejor oferta del mercado libre de electricidad para ti, nos encargamos de todo y tú no tienes que llamar a nadie.
        </p>
      </div>
      <div className="companies-ticker">
        <div className="companies-track">
          {doubled.map((company, index) => (
            <span key={index} className="company-pill">
              {company}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============================================================
// COMPONENTE: CTA Section (Urgencia)
// ============================================================
const CTASection = () => (
  <section className="cta-section py-20 md:py-28" data-testid="cta-section">
    <div className="container mx-auto px-4 md:px-6 max-w-4xl text-center relative z-10">
      <div className="reveal">
        <div className="inline-flex items-center gap-2 bg-solar-yellow/20 text-solar-yellow px-4 py-2 rounded-full mb-6 text-xs md:text-sm font-bold font-oswald uppercase tracking-wide">
          <Sparkles className="w-4 h-4 text-solar-yellow" />
          Estudio de ahorro 100% gratuito y sin compromiso
        </div>
        <h2 className="text-3xl md:text-5xl font-bold tracking-tight font-oswald uppercase text-white mb-6 leading-tight">
          ¿Cuánto podrías ahorrar <span className="text-gradient">este mismo mes</span>?
        </h2>
        <p className="text-lg text-slate-300 font-manrope mb-10 max-w-2xl mx-auto leading-relaxed">
          Más de 500 familias y negocios de Alicante ya disfrutan de facturas más bajas. Solo nos enviaron una foto de su factura de luz y nosotros hicimos el resto. ¿Quieres ver tu ahorro real?
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <a
            href="#contacto"
            onClick={(e) => {
              e.preventDefault();
              document.getElementById("contacto")?.scrollIntoView({ behavior: "smooth" });
            }}
            className="btn-primary btn-pulse text-slate-900 font-bold px-10 py-4 rounded-full text-lg uppercase font-oswald tracking-wide inline-flex items-center justify-center gap-2"
          >
            Solicitar mi estudio gratis
            <ArrowRight className="w-5 h-5" />
          </a>
          <a
            href={`tel:${CONTACT_INFO.phone.replace(/\s/g, "")}`}
            className="flex items-center justify-center gap-2 border-2 border-white/20 hover:border-solar-yellow hover:text-solar-yellow text-white font-bold px-10 py-4 rounded-full transition-all text-lg"
          >
            <Phone className="w-5 h-5 text-solar-yellow" />
            Llamar al {CONTACT_INFO.phone}
          </a>
        </div>
        <p className="text-slate-400 text-sm mt-6 font-manrope">
          Estudio detallado sin coste en menos de 24h · Trato de persona a persona
        </p>
      </div>
    </div>
  </section>
);

// ============================================================
// COMPONENTE: FAQ Section
// ============================================================
const FAQSection = () => {
  const faqs = [
    {
      question: "¿El análisis de mi factura tiene algún coste o compromiso?",
      answer:
        "No, en absoluto. Realizar la comparación de tarifas, el estudio de potencia y el cálculo de viabilidad de placas solares es 100% gratuito y sin compromiso. Te damos un número de ahorro real de forma honesta, y tú decides libremente qué hacer.",
    },
    {
      question: "¿Cuánto puedo ahorrar realmente con placas solares?",
      answer:
        "La mayoría de nuestros clientes residenciales ahorran entre un 70% y un 90% en su factura de luz mensual. En el caso de empresas y naves industriales, el ahorro directo en consumo eléctrico reduce significativamente los costes fijos mensuales.",
    },
    {
      question: "Si vivo en un piso o no quiero hacer obras, ¿puedo ahorrar?",
      answer:
        "¡Sí, por supuesto! De hecho, nuestro simulador tiene la opción dedicada a Pisos ('Sin Obras'). En ese caso, analizamos tu factura, detectamos si pagas potencia de más o cargos fantasma y te cambiamos gratis a la tarifa más barata. Es ahorro directo sin instalar absolutamente nada.",
    },
    {
      question: "¿Cuánto se tarda en hacer el cambio de compañía y hay cortes de luz?",
      answer:
        "El cambio de comercializadora es un trámite puramente administrativo y tarda de 1 a 7 días hábiles. Es 100% seguro: no hay ningún tipo de corte de luz ni cortes de suministro en ningún momento del proceso. Además, nosotros gestionamos todo el papeleo por ti.",
    },
    {
      question: "¿Qué garantía tienen los paneles solares que instaláis?",
      answer:
        "Al ser instaladores autorizados y experimentados, solo trabajamos con marcas líderes internacionales. Ofrecemos una garantía oficial del fabricante de hasta 25 años en los paneles solares. Nuestro diseño a medida asegura que tu sistema dure y funcione al máximo nivel durante décadas.",
    },
    {
      question: "¿Qué os tengo que enviar para hacer el estudio de ahorro?",
      answer:
        "Solo necesitamos una foto o el archivo PDF de tu última factura de luz completa. Con ella podemos ver tu consumo por horas y realizar un estudio satelital de tu tejado. Puedes enviárnosla de forma súper cómoda por WhatsApp o por email.",
    },
    {
      question: "¿Dónde está vuestra oficina y qué zonas atendéis?",
      answer:
        "Nuestra oficina física principal está en el centro de Elche (Carrer Dr. Caro, 87). Atendemos a clientes de toda la provincia de Alicante y la Región de Murcia. Para instalaciones solares, trabajamos en toda la Comunidad Valenciana.",
    },
  ];

  return (
    <section
      id="faq"
      className="py-20 md:py-28 bg-white"
      data-testid="faq-section"
    >
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Columna izquierda */}
          <div className="reveal-left">
            <div className="section-divider mb-6" />
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight font-oswald uppercase text-slate-900 mb-6">
              Preguntas <span className="text-gradient">frecuentes</span>
            </h2>
            <p className="text-lg text-slate-650 font-manrope mb-8 leading-relaxed">
              Resolvemos tus dudas con palabras sencillas y directas. Si tienes otra pregunta, llámanos o escríbenos directamente por WhatsApp y te responderemos en minutos.
            </p>
            <div className="space-y-4">
              <a
                href={`tel:${CONTACT_INFO.phone.replace(/\s/g, "")}`}
                className="flex items-center gap-4 p-5 bg-slate-50 rounded-2xl hover:bg-solar-yellow/5 border border-slate-100 hover:border-solar-yellow/30 transition-all group"
              >
                <div className="bg-solar-yellow/15 w-12 h-12 rounded-xl flex items-center justify-center group-hover:bg-solar-yellow transition-colors">
                  <Phone className="w-5 h-5 text-solar-yellow group-hover:text-slate-900 transition-colors" />
                </div>
                <div>
                  <p className="font-bold text-slate-900 font-oswald text-sm md:text-base">
                    Llámanos por teléfono
                  </p>
                  <p className="text-solar-yellow font-bold text-sm md:text-base">
                    {CONTACT_INFO.phone}
                  </p>
                </div>
              </a>
              <a
                href={`https://wa.me/${CONTACT_INFO.whatsapp}?text=${encodeURIComponent("Hola, tengo una pregunta y me gustaría que me ayudarais a resolverla.")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 p-5 bg-slate-50 rounded-2xl hover:bg-emerald-50 border border-slate-100 hover:border-emerald-200 transition-all group"
              >
                <div className="bg-emerald-100 w-12 h-12 rounded-xl flex items-center justify-center group-hover:bg-emerald-500 transition-colors">
                  <MessageSquare className="w-5 h-5 text-emerald-600 group-hover:text-white transition-colors" />
                </div>
                <div>
                  <p className="font-bold text-slate-900 font-oswald text-sm md:text-base">
                    Escríbenos un WhatsApp
                  </p>
                  <p className="text-emerald-600 font-bold text-xs md:text-sm">
                    Respuesta inmediata de un asesor humano
                  </p>
                </div>
              </a>
            </div>
          </div>

          {/* Columna derecha: Accordion */}
          <div className="reveal-right">
            <Accordion type="single" collapsible className="space-y-3">
              {faqs.map((faq, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className="faq-item bg-white rounded-xl overflow-hidden shadow-sm"
                  data-testid={`faq-item-${index}`}
                >
                  <AccordionTrigger className="px-6 py-4 text-left font-bold text-slate-900 hover:bg-solar-yellow/5 faq-trigger text-sm md:text-base font-manrope">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-5 text-slate-650 font-manrope text-sm leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============================================================
// COMPONENTE: Contact Section
// ============================================================
const ContactSection = () => {
  return (
    <section
      id="contacto"
      className="py-20 md:py-28 bg-slate-50"
      data-testid="contact-section"
    >
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="text-center max-w-3xl mx-auto mb-12 reveal">
          <div className="section-divider mx-auto mb-6" />
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight font-oswald uppercase text-slate-900 mb-4">
            Solicita tu <span className="text-gradient">estudio de ahorro gratuito</span>
          </h2>
          <p className="text-lg text-slate-600 font-manrope">
            Envíanos tus datos de forma rápida y un asesor experto diseñará tu propuesta a medida de forma 100% gratuita y sin ningún compromiso.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-10 items-start">
          {/* Formulario — ocupa 3 columnas */}
          <div className="lg:col-span-3 reveal-left">
            <div className="form-container bg-white">
              <iframe
                src="https://api.leadconnectorhq.com/widget/form/TnesM0dyMn0f6HAl8UbY"
                style={{
                  width: "100%",
                  height: "100%",
                  border: "none",
                  minHeight: "720px",
                  display: "block",
                }}
                id="inline-TnesM0dyMn0f6HAl8UbY"
                data-layout="{'id':'INLINE'}"
                data-trigger-type="alwaysShow"
                data-trigger-value=""
                data-activation-type="alwaysActivated"
                data-activation-value=""
                data-deactivation-type="neverDeactivate"
                data-deactivation-value=""
                data-form-name="Formulario Captación de Leads Web AMQL"
                data-height="957"
                data-layout-iframe-id="inline-TnesM0dyMn0f6HAl8UbY"
                data-form-id="TnesM0dyMn0f6HAl8UbY"
                title="Formulario Captación de Leads Web AMQL"
              />
            </div>
          </div>

          {/* Info de contacto — ocupa 2 columnas */}
          <div className="lg:col-span-2 space-y-5 reveal-right">
            {/* Tarjetas de contacto */}
            {[
              {
                icon: Phone,
                label: "Llámanos",
                value: CONTACT_INFO.phone,
                href: `tel:${CONTACT_INFO.phone.replace(/\s/g, "")}`,
              },
              {
                icon: Mail,
                label: "Correo Electrónico",
                value: CONTACT_INFO.email,
                href: `mailto:${CONTACT_INFO.email}`,
              },
              {
                icon: MapPin,
                label: "Nuestra Oficina",
                value: CONTACT_INFO.address,
                href: CONTACT_INFO.mapsUrl,
              },
              {
                icon: Clock,
                label: "Horario de Atención",
                value: CONTACT_INFO.hours,
                href: null,
              },
            ].map((item, i) => (
              <div
                key={i}
                className="contact-info-card bg-white p-5 rounded-2xl flex items-center gap-4 shadow-sm border border-slate-100"
              >
                <div className="bg-solar-yellow/15 w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-5 h-5 text-solar-yellow" />
                </div>
                <div>
                  <p className="text-xs text-slate-400 font-manrope uppercase tracking-wide">
                    {item.label}
                  </p>
                  {item.href ? (
                    <a
                      href={item.href}
                      target={item.href.startsWith("http") ? "_blank" : undefined}
                      rel={item.href.startsWith("http") ? "noopener noreferrer" : undefined}
                      className="font-bold text-slate-900 hover:text-solar-yellow transition-colors text-sm"
                    >
                      {item.value}
                    </a>
                  ) : (
                    <p className="font-bold text-slate-900 text-sm">
                      {item.value}
                    </p>
                  )}
                </div>
              </div>
            ))}

            {/* Mapa */}
            <div className="map-container h-56 reveal border border-slate-200">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3128.5!2d-0.7035603!3d38.2696265!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xd63b733564491e1%3A0x183bb1f56824793f!2sCarrer%20Dr.%20Caro%2C%2087%2C%2003201%20Elx%2C%20Alicante!5e0!3m2!1ses!2ses!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Ubicación física de la oficina de AlgoMásQueLuz en Elche"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============================================================
// COMPONENTE: Footer
// ============================================================
const Footer = () => {
  const year = new Date().getFullYear();

  const handleLogoClick = (e) => {
    e.preventDefault();
    const topElement = document.getElementById("inicio");
    if (topElement) {
      topElement.scrollIntoView({ behavior: "smooth" });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <footer
      className="bg-[#0A192F] text-white pt-16 pb-8 border-t border-white/5"
      data-testid="footer"
    >
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="grid md:grid-cols-4 gap-10 mb-12">
          {/* Logo & Descripción */}
          <div className="md:col-span-2">
            <a
              href="#inicio"
              onClick={handleLogoClick}
              className="inline-block mb-5 group active:scale-95 transition-transform duration-300"
              aria-label="Volver al inicio - AlgoMásQueLuz"
            >
              <div className="bg-white rounded-2xl p-3 inline-block hover:shadow-lg hover:shadow-solar-yellow/20 transition-all">
                <img
                  src={LOGO_URL}
                  alt="AlgoMásQueLuz"
                  className="h-10 object-contain"
                />
              </div>
            </a>
            <p className="text-slate-300 font-manrope text-sm leading-relaxed mb-5 max-w-xs">
              Tu gestor energético e instalaciones solares de confianza en Elche y Alicante. Más de 20 años de experiencia sólida ayudando a hogares y empresas a ahorrar de forma real.
            </p>
            {/* Redes sociales */}
            <div className="flex gap-3">
              {[
                {
                  href: SOCIAL_LINKS.instagram,
                  label: "Instagram",
                  svg: (
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                  ),
                },
                {
                  href: SOCIAL_LINKS.facebook,
                  label: "Facebook",
                  svg: (
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                  ),
                },
                {
                  href: SOCIAL_LINKS.tiktok,
                  label: "TikTok",
                  svg: (
                    <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
                  ),
                },
              ].map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                  className="social-icon w-10 h-10 bg-slate-800 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-900"
                >
                  <svg
                    className="w-5 h-5"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    {social.svg}
                  </svg>
                </a>
              ))}
            </div>
          </div>

          {/* Servicios */}
          <div>
            <h3 className="text-base font-bold font-oswald mb-5 text-white uppercase tracking-wide">
              Servicios
            </h3>
            <nav className="flex flex-col gap-2.5">
              {[
                "Placas Solares Residenciales",
                "Autoconsumo Industrial",
                "Optimización de Facturas",
                "Gestión de Burocracia",
              ].map((item) => (
                <a
                  key={item}
                  href="#servicios"
                  onClick={(e) => {
                    e.preventDefault();
                    document.getElementById("servicios")?.scrollIntoView({ behavior: "smooth" });
                  }}
                  className="footer-link text-slate-400 hover:text-white text-sm font-manrope transition-colors"
                >
                  {item}
                </a>
              ))}
            </nav>
          </div>

          {/* Contacto */}
          <div>
            <h3 className="text-base font-bold font-oswald mb-5 text-white uppercase tracking-wide">
              Contacto
            </h3>
            <div className="space-y-3">
              <a
                href={`tel:${CONTACT_INFO.phone.replace(/\s/g, "")}`}
                className="flex items-center gap-2 text-slate-400 hover:text-solar-yellow transition-colors text-sm font-manrope"
              >
                <Phone className="w-4 h-4 text-solar-yellow flex-shrink-0" />
                {CONTACT_INFO.phone}
              </a>
              <a
                href={`mailto:${CONTACT_INFO.email}`}
                className="flex items-center gap-2 text-slate-400 hover:text-solar-yellow transition-colors text-sm font-manrope"
              >
                <Mail className="w-4 h-4 text-solar-yellow flex-shrink-0" />
                {CONTACT_INFO.email}
              </a>
              <div className="flex items-start gap-2 text-slate-400 text-sm font-manrope">
                <MapPin className="w-4 h-4 text-solar-yellow flex-shrink-0 mt-0.5" />
                <span>{CONTACT_INFO.address}</span>
              </div>
              <div className="flex items-start gap-2 text-slate-400 text-sm font-manrope">
                <Clock className="w-4 h-4 text-solar-yellow flex-shrink-0 mt-0.5" />
                <span>{CONTACT_INFO.hours}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-white/5 pt-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-slate-500 text-sm font-manrope text-center md:text-left">
              © {year} AlgoMásQueLuz S.L. · Todos los derechos reservados · Energía Solar y Autoconsumo en Alicante
            </p>
            <div className="flex items-center gap-6">
              {["Política de Privacidad", "Términos y Condiciones", "Cookies"].map(
                (item) => (
                  <a
                    key={item}
                    href="#"
                    className="text-slate-500 hover:text-slate-350 text-xs font-manrope transition-colors"
                  >
                    {item}
                  </a>
                )
              )}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

// ============================================================
// COMPONENTE: Sticky CTA Bar (Mobile)
// ============================================================
const StickyCTABar = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsVisible(window.scrollY > 600);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  if (!isVisible) return null;

  return (
    <div className="sticky-cta-bar fixed bottom-0 left-0 right-0 z-40 md:hidden px-4 py-3 flex gap-3">
      <a
        href={`tel:${CONTACT_INFO.phone.replace(/\s/g, "")}`}
        className="flex-1 flex items-center justify-center gap-2 border-2 border-slate-200 bg-white text-slate-800 font-bold py-3 rounded-full text-sm font-oswald uppercase shadow-sm"
      >
        <Phone className="w-4 h-4 text-solar-yellow" />
        Llamar
      </a>
      <a
        href="#contacto"
        onClick={(e) => {
          e.preventDefault();
          document.getElementById("contacto")?.scrollIntoView({ behavior: "smooth" });
        }}
        className="flex-2 flex-grow btn-primary text-slate-900 font-bold py-3 rounded-full text-sm uppercase font-oswald tracking-wide text-center flex items-center justify-center gap-2"
      >
        Estudio Gratis
        <ArrowRight className="w-4 h-4" />
      </a>
    </div>
  );
};

// ============================================================
// APP PRINCIPAL
// ============================================================
export default function App() {
  const scrollProgress = useScrollProgress();
  useScrollReveal();

  return (
    <div className="min-h-screen bg-white" id="inicio">
      {/* Barra de progreso de scroll */}
      <div
        className="scroll-progress"
        style={{ width: `${scrollProgress}%` }}
      />

      <Header />

      <main>
        <HeroSection />
        <PillarsSection />
        <NosotrosSection />
        <ProblemSection />
        <ServicesSection />
        <ProcessSection />
        <TestimonialsSection />
        <BenefitsSection />
        <CompaniesSection />
        <CTASection />
        <FAQSection />
        <ContactSection />
      </main>

      <Footer />

      {/* Componentes flotantes */}
      <WhatsAppFloating />
      <StickyCTABar />
    </div>
  );
}
